import { Injectable, signal } from '@angular/core';

/**
 * Holds the optional admin token for the Tracking Demo backend surface.
 *
 * The FastAPI `live_call_agent` app runs its `/api/tracking/*` surface OPEN by default
 * (the CRM already authorizes the user); when `LIVE_CALL_ADMIN_TOKEN` is configured
 * server-side, this token is sent as X-Admin-Token. It lives in sessionStorage (never
 * in the bundle) so a refresh keeps working but a closed tab clears it.
 *
 * This is the ONLY place the token is read/written — a future real identity system
 * replaces this service's internals without touching the api/socket/page code.
 */
const STORAGE_KEY = 'trackingAdminToken';

@Injectable({ providedIn: 'root' })
export class TrackingAdminTokenService {
  private readonly _token = signal<string | null>(this.read());
  readonly token = this._token.asReadonly();

  private read(): string | null {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  set(token: string): void {
    const t = (token || '').trim();
    if (!t) return;
    try { sessionStorage.setItem(STORAGE_KEY, t); } catch { /* private mode */ }
    this._token.set(t);
  }

  /** Called on a 401 — clears the stored (now invalid) token. */
  invalidate(): void {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    this._token.set(null);
  }
}
